import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AccessibleDialog from "./modal/AccessibleDialog";
import { appBackendErrorOf } from "../services/appBackend";
import { startCheckout } from "../services/billingApi";
import {
  CatalogueProduct,
  CheckoutRequest,
  CreditPack,
  ProductKey,
} from "../types/billing";
import {
  BuyCreditsPreset,
  WalletBalance,
  WalletCatalogue,
} from "../types/wallet";
import { checkoutMessage } from "../utils/checkoutMessage";
import { SALES_EMAIL, SALES_MAILTO } from "../utils/contact";
import {
  formatCreditRate,
  formatPackAmount,
  viewerPackCurrency,
} from "../utils/packCurrency";
import {
  clampCompanies,
  entityPricingOf,
  runCreditsFor,
} from "../utils/productCatalogue";
import {
  maxRunsPerCheckout,
  packCheckoutRequest,
  packsCheaperThan,
  parseRunCount,
  productCheckoutRequest,
  quoteProductPurchase,
  rateValue,
} from "../utils/purchaseQuote";
import { markPurchaseInFlight } from "../utils/purchaseMarker";
import "../styles/BuyCreditsModal.scss";

/**
 * Buy Credits: a pack, or Credits sized to a number of runs of one product. Both end at Stripe's
 * hosted Checkout, and everything bought lands in the one wallet.
 *
 * Every name, credit cost and price here comes from the catalogue the provider loaded; the
 * dialog only does the arithmetic. It opens from the header, the landing page and the product
 * pages' "not enough Credits" dialog, each with a preset saying which tab and product to start on.
 */

interface BuyCreditsModalProps {
  preset: BuyCreditsPreset;
  balance: WalletBalance;
  walletChecked: boolean;
  catalogue: WalletCatalogue;
  /** A checkout was refused because what it asked for is no longer on sale. */
  onCatalogueStale: () => void;
  /**
   * The checkout is open and the browser is about to leave for Stripe, for a purchase started
   * from `returnProduct` (or from no product): the last moment to keep that page's draft.
   */
  onLeavingForCheckout?: (returnProduct: ProductKey | null) => void;
  onClose: () => void;
}

type Tab = "packs" | "product";

const count = (n: number) => n.toLocaleString("en-US");

const plural = (n: number, one: string, many: string) =>
  `${count(n)} ${n === 1 ? one : many}`;

/** A preset quantity is a hint from another page: anything but a whole number of 1+ is 1. */
const initialRuns = (quantity: number | undefined): string =>
  quantity !== undefined && Number.isSafeInteger(quantity) && quantity >= 1
    ? String(quantity)
    : "1";

interface ContinueToProps {
  label: string;
  route: string;
  disabled: boolean;
  onClose: () => void;
}

/**
 * The way past the dialog, to where the buyer was going when it stepped in. A component of its
 * own so that only a dialog opened with `continueTo` reads the router. The wallet provider sits
 * inside the app's router, so there always is one; everything else in the dialog leaves for
 * Stripe rather than for a route, and needs none.
 */
const ContinueTo: React.FC<ContinueToProps> = ({
  label,
  route,
  disabled,
  onClose,
}) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="bc-link bc-continue"
      disabled={disabled}
      onClick={() => {
        onClose();
        navigate(route);
      }}
    >
      {label}
    </button>
  );
};

const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({
  preset,
  balance,
  walletChecked,
  catalogue,
  onCatalogueStale,
  onLeavingForCheckout,
  onClose,
}) => {
  const currency = useMemo(() => viewerPackCurrency(), []);
  const ids = useId();

  const packs: CreditPack[] =
    catalogue.packs.status === "ready" ? catalogue.packs.packs : [];
  const products: CatalogueProduct[] =
    catalogue.products.status === "ready" ? catalogue.products.products : [];
  const forSale = products.filter((product) => product.purchase !== null);

  // `null` until the user picks a tab; until then the preset decides.
  const [chosenTab, setChosenTab] = useState<Tab | null>(null);
  const [selected, setSelected] = useState<ProductKey | undefined>(
    preset.product,
  );
  const [runsText, setRunsText] = useState(() => initialRuns(preset.quantity));
  const [companies, setCompanies] = useState<number | undefined>(
    preset.companies,
  );
  // Which purchase is on its way to Stripe: "product", or the pack's lookup key.
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // State reaches the buttons a render later, which is not soon enough to stop the second
  // click of a double click from opening a second checkout.
  const buying = useRef(false);

  useEffect(() => {
    // Back from Stripe's page can restore this page from the browser's back-forward cache
    // exactly as it left, dialog and all, which would leave every button disabled and one of
    // them still reading "Redirecting…".
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      buying.current = false;
      setBusy(null);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // The tab and product actually shown. Worked out on every render rather than stored, because
  // the catalogue may still be on its way when the dialog opens, and a reload of it can take
  // away what was chosen while the dialog is open.
  const tabs: Tab[] = [
    ...(packs.length > 0 ? (["packs"] as Tab[]) : []),
    ...(forSale.length > 0 ? (["product"] as Tab[]) : []),
  ];
  // A preset product that cannot be bought per product sends the dialog to the packs, which
  // can still cover the run.
  const presetProductUnsold =
    preset.product !== undefined &&
    !forSale.some((candidate) => candidate.product === preset.product);
  const presetTab: Tab =
    preset.tab === "product" && presetProductUnsold && packs.length > 0
      ? "packs"
      : preset.tab;
  const wantedTab = chosenTab ?? presetTab;
  // Nothing is shown until both packs and products have answered: a tab picked on half the
  // catalogue could change under the buyer's eyes a moment later. A reload keeps both on
  // screen, so this waits only the first time.
  const tab: Tab | null =
    catalogue.billing === "loading"
      ? null
      : tabs.includes(wantedTab)
        ? wantedTab
        : (tabs[0] ?? null);
  const product =
    forSale.find((candidate) => candidate.product === selected) ??
    forSale[0] ??
    null;

  const locked = busy !== null;

  // Why the dialog opened ("You need 40 more credits for this run"). It heads the product tab;
  // it heads the packs as well when that is where the preset landed, so a product that cannot
  // be bought per product still explains itself. A dialog that stepped in on the way to a
  // product page offers the way on from the same banner.
  const contextBanner =
    preset.context || preset.continueTo ? (
      <div className="bc-context">
        {preset.context && <p className="bc-context-text">{preset.context}</p>}
        {preset.continueTo && (
          <ContinueTo
            label={preset.continueTo.label}
            route={preset.continueTo.route}
            disabled={locked}
            onClose={onClose}
          />
        )}
      </div>
    ) : null;
  const contextOnPacks =
    (tabs.includes(presetTab) ? presetTab : tabs[0]) === "packs";

  const checkout = async (
    request: CheckoutRequest,
    busyKey: string,
    credits: number,
  ): Promise<void> => {
    if (buying.current || !walletChecked) return;
    buying.current = true;
    setBusy(busyKey);
    setError(null);

    try {
      const url = await startCheckout(request);
      // The app unloads on the way to Stripe; this note is what it finds on the way back: the
      // page to go back to, and what the purchase adds up to in that page's runs. A run whose
      // cost grows with its companies is counted only when the dialog knows the scan's size:
      // from the shortfall it was opened for, or from buying that product at a chosen size.
      // Otherwise (a pack from the header's Top up) the stepper holds only its default, and
      // no run count is claimed.
      const returnTo = preset.returnProduct
        ? (products.find((known) => known.product === preset.returnProduct) ??
          null)
        : null;
      const scanSizeKnown =
        preset.companies !== undefined ||
        (returnTo?.purchase != null &&
          request.lookup_key === returnTo.purchase.lookup_key);
      const perRunCredits =
        returnTo && (entityPricingOf(returnTo) === null || scanSizeKnown)
          ? runCreditsFor(returnTo, companies)
          : null;
      markPurchaseInFlight(
        preset.returnProduct ?? null,
        returnTo?.name ?? null,
        { credits, perRunCredits },
      );
      // Only now, with a checkout to go to: a refused one leaves the page where it is, input
      // and all, and nothing needs keeping.
      onLeavingForCheckout?.(preset.returnProduct ?? null);
      // The buttons stay as they are: this page is on its way to Stripe.
      window.location.assign(url);
    } catch (failure) {
      const { code, detail } = appBackendErrorOf(failure);
      setError(checkoutMessage(code, detail));
      buying.current = false;
      setBusy(null);
      if (code === "BILLING_UNKNOWN_PACK") {
        // What was on sale changed in Stripe while the dialog was open. The message stays up
        // over the fresh catalogue, and whatever went is no longer offered.
        onCatalogueStale();
      }
    }
  };

  /* ------------------------------------------------------------------ tabs */

  const tabId = (which: Tab) => `${ids}-tab-${which}`;
  const panelId = `${ids}-panel`;

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (tabs.length < 2 || !tab) return;
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const index = tabs.indexOf(tab);
    const next =
      event.key === "Home"
        ? tabs[0]
        : event.key === "End"
          ? tabs[tabs.length - 1]
          : tabs[
              (index + (event.key === "ArrowRight" ? 1 : tabs.length - 1)) %
                tabs.length
            ];
    setChosenTab(next);
    document.getElementById(tabId(next))?.focus();
  };

  const renderTabs = () => (
    <div className="bc-tabs" role="tablist" aria-label="How to buy">
      {tabs.map((which) => (
        <button
          key={which}
          id={tabId(which)}
          type="button"
          role="tab"
          className={`bc-tab ${tab === which ? "active" : ""}`}
          aria-selected={tab === which}
          aria-controls={panelId}
          tabIndex={tab === which ? 0 : -1}
          disabled={locked}
          onClick={() => setChosenTab(which)}
          onKeyDown={onTabKeyDown}
        >
          {which === "packs" ? "Buy a Credit Pack" : "Buy a Product"}
        </button>
      ))}
    </div>
  );

  /* ------------------------------------------------------------ packs tab */

  const renderPacks = () => (
    <>
      <div className="bc-pack-cards">
        {packs.map((pack) => (
          <div className="bc-pack-card" key={pack.lookup_key}>
            {/* The number over the word on every card, so the cards stay level however wide
                the number: "1,000 Credits" on one line wrapped where "50 Credits" did not. */}
            <h3>
              <span className="bc-pack-credits">{count(pack.credits)}</span>{" "}
              <span className="bc-pack-unit">Credits</span>
            </h3>
            <div className="bc-pack-amount">
              {formatPackAmount(pack.amount, currency)}
            </div>
            {pack.credits > 0 && (
              <div className="bc-pack-rate">
                {formatCreditRate(pack.amount / pack.credits, currency)} per
                credit
              </div>
            )}
            <button
              type="button"
              className="bc-button"
              aria-label={`Buy ${pack.name}`}
              disabled={!walletChecked || locked}
              onClick={() =>
                checkout(
                  packCheckoutRequest(pack, preset.returnProduct),
                  pack.lookup_key,
                  pack.credits,
                )
              }
            >
              {busy === pack.lookup_key ? "Redirecting…" : "Buy"}
            </button>
          </div>
        ))}
      </div>
      <p className="bc-note">
        Charged in USD, GBP, EUR or CHF depending on your location, plus
        applicable tax.
      </p>
    </>
  );

  /* ---------------------------------------------------------- product tab */

  const renderProduct = (chosen: CatalogueProduct) => {
    const pricing = entityPricingOf(chosen);
    const scans = pricing !== null;
    const [one, many] = scans ? ["scan", "scans"] : ["run", "runs"];
    const perRun = clampCompanies(chosen, companies);

    const runs = parseRunCount(runsText);
    const maxRuns = maxRunsPerCheckout(chosen, perRun);
    let runsError: string | null = null;
    if (runs === null) {
      runsError = "Enter a whole number of 1 or more.";
    } else if (runs > maxRuns) {
      runsError =
        maxRuns < 1
          ? "This is more than one checkout can take."
          : `One checkout can take up to ${plural(maxRuns, one, many)}.`;
    }
    const quote =
      runs !== null && runsError === null
        ? quoteProductPurchase(chosen, runs, perRun)
        : null;
    const cheaper = packsCheaperThan(packs, chosen);

    const runsId = `${ids}-runs`;
    const runsErrorId = `${ids}-runs-error`;
    const companiesLabelId = `${ids}-companies`;

    const stepRuns = (delta: number) => {
      const current = runs ?? 1;
      const next =
        runs === null
          ? 1
          : Math.min(Math.max(1, current + delta), Math.max(1, maxRuns));
      setRunsText(String(next));
    };

    return (
      <>
        {contextBanner}
        <fieldset className="bc-products" disabled={locked}>
          <legend className="bc-label">Product</legend>
          {forSale.map((candidate) => {
            const candidatePricing = entityPricingOf(candidate);
            return (
              <label
                key={candidate.product}
                className={`bc-product ${candidate.product === chosen.product ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name={`${ids}-product`}
                  value={candidate.product}
                  checked={candidate.product === chosen.product}
                  onChange={() => setSelected(candidate.product)}
                />
                <span className="bc-product-name">{candidate.name}</span>
                <span className="bc-product-cost">
                  {candidatePricing
                    ? `${count(candidate.run_credits)} credits per scan of up to ${candidatePricing.included} companies`
                    : `${count(candidate.run_credits)} credits per run`}
                </span>
              </label>
            );
          })}
        </fieldset>

        <div className="bc-field">
          <label className="bc-label" htmlFor={runsId}>
            Number of Executions
          </label>
          <div className="bc-stepper">
            <button
              type="button"
              className="bc-step"
              aria-label={`One ${one} fewer`}
              disabled={locked || (runs !== null && runs <= 1)}
              onClick={() => stepRuns(-1)}
            >
              −
            </button>
            <input
              id={runsId}
              className="bc-runs-input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={runsText}
              disabled={locked}
              aria-invalid={runsError !== null}
              aria-describedby={runsError ? runsErrorId : undefined}
              onChange={(event) => setRunsText(event.target.value)}
            />
            <button
              type="button"
              className="bc-step"
              aria-label={`One ${one} more`}
              disabled={locked || (runs !== null && runs >= maxRuns)}
              onClick={() => stepRuns(1)}
            >
              +
            </button>
          </div>
          {runsError && (
            <p id={runsErrorId} className="bc-field-error">
              {runsError}
            </p>
          )}
        </div>

        {pricing && (
          <div className="bc-field">
            <span className="bc-label" id={companiesLabelId}>
              Companies per scan
            </span>
            <div
              className="bc-stepper"
              role="group"
              aria-labelledby={companiesLabelId}
            >
              <button
                type="button"
                className="bc-step"
                aria-label="One company fewer"
                disabled={locked || perRun <= pricing.included}
                onClick={() => setCompanies(perRun - 1)}
              >
                −
              </button>
              <output className="bc-companies" aria-live="polite">
                {perRun}
              </output>
              <button
                type="button"
                className="bc-step"
                aria-label="One company more"
                disabled={locked || perRun >= pricing.max}
                onClick={() => setCompanies(perRun + 1)}
              >
                +
              </button>
            </div>
            <p className="bc-hint">
              {pricing.included} included · each extra company{" "}
              {count(pricing.extraCredits)} credits · up to {pricing.max}
            </p>
          </div>
        )}

        {quote && (
          <dl className="bc-summary">
            <div className="bc-line">
              <dt>
                {count(quote.runs)} × {chosen.name}
              </dt>
              <dd>{formatPackAmount(quote.runsAmount, currency)}</dd>
            </div>
            {quote.addonUnits > 0 && (
              <div className="bc-line">
                <dt>{count(quote.addonUnits)} × Additional company</dt>
                <dd>{formatPackAmount(quote.addonAmount, currency)}</dd>
              </div>
            )}
            <div className="bc-line">
              <dt>Credits added to your wallet</dt>
              <dd className="bc-credits">+{count(quote.credits)}</dd>
            </div>
            {balance.status === "known" && (
              <div className="bc-line">
                <dt>Balance after purchase</dt>
                <dd>
                  {count(balance.credits)} →{" "}
                  {count(balance.credits + quote.credits)}
                </dd>
              </div>
            )}
            <div className="bc-line bc-total">
              <dt>Total</dt>
              <dd>{formatPackAmount(quote.total, currency)}</dd>
            </div>
          </dl>
        )}

        {cheaper && (
          <p className="bc-packs-cheaper">
            Credit packs cost less per credit: from{" "}
            {formatCreditRate(rateValue(cheaper.pack), currency)} vs{" "}
            {formatCreditRate(rateValue(cheaper.product), currency)}.{" "}
            <button
              type="button"
              className="bc-link"
              disabled={locked}
              onClick={() => {
                setChosenTab("packs");
                // This link goes with the tab it sits on. Focus follows it to the packs' own tab
                // rather than falling out of the dialog to the page behind it.
                document.getElementById(tabId("packs"))?.focus();
              }}
            >
              See credit packs
            </button>
          </p>
        )}

        <div className="bc-footer">
          <p className="bc-note">
            Credits go into your wallet and can be spent on any product.
            Currency and tax are set at checkout.
          </p>
          <button
            type="button"
            className="bc-button bc-cta"
            disabled={!walletChecked || locked || quote === null}
            onClick={() => {
              if (runs === null || quote === null) return;
              const request = productCheckoutRequest(
                chosen,
                runs,
                perRun,
                preset.returnProduct,
              );
              if (request) checkout(request, "product", quote.credits);
            }}
          >
            {busy === "product" ? "Redirecting…" : "Continue to payment"}
          </button>
        </div>
      </>
    );
  };

  /* ----------------------------------------------------------------- body */

  return (
    <AccessibleDialog
      title="Buy Credits"
      onClose={onClose}
      closeDisabled={locked}
      className="buy-credits-dialog"
    >
      {error && (
        <p className="bc-error" role="alert">
          {error}
        </p>
      )}

      {tab !== null && (
        <>
          {renderTabs()}
          <div
            className="bc-panel"
            id={panelId}
            role="tabpanel"
            aria-labelledby={tabId(tab)}
          >
            {tab === "packs" && contextOnPacks && contextBanner}
            {tab === "packs" && renderPacks()}
            {tab === "product" && product && renderProduct(product)}
          </div>
        </>
      )}

      {/* Nothing is said while the catalogue is on its way; once it has answered with nothing
          to sell, the page's usual way to buy Credits is the only one left. */}
      {tab === null && catalogue.billing === "unavailable" && (
        <p className="bc-unavailable">
          Please contact <a href={SALES_MAILTO}>{SALES_EMAIL}</a> to buy
          Credits.
        </p>
      )}
    </AccessibleDialog>
  );
};

export default BuyCreditsModal;
